/**
 * Mega-menu (Services / Industries): page backdrop + GSAP open/close.
 * Uses one shared leave timer + sibling close so fast hovers between items
 * do not fade the backdrop while another panel is open.
 */
import gsap from "gsap";

const EASE_OUT = "power3.out";
const EASE_IN = "power2.in";
const LEAVE_DELAY_MS = 75;

const MEGA_TRIGGER_SELECTOR =
	".header__nav li.nav-services, .header__nav li.nav-industries";
const DESKTOP_MQ = "(min-width: 1025px)";

function isDesktopMegaNav() {
	return window.matchMedia(DESKTOP_MQ).matches;
}

/** @type {ReturnType<typeof createItemApi>[]} */
let itemApis = [];
let globalLeaveTimer = null;

function clearGlobalLeaveTimer() {
	if (globalLeaveTimer !== null) {
		window.clearTimeout(globalLeaveTimer);
		globalLeaveTimer = null;
	}
}

function isAnyMegaItemHovered() {
	return itemApis.some(({ item }) => item.matches(":hover"));
}

function backdropIsVisiblyOn(backdrop) {
	if (!backdrop) {
		return false;
	}
	return parseFloat(String(gsap.getProperty(backdrop, "opacity"))) > 0.12;
}

/**
 * @param {HTMLElement} item
 * @param {HTMLElement | null} backdrop
 * @param {HTMLElement[]} allMegaItems
 */
function createItemApi(item, backdrop, allMegaItems) {
	const dropdown = item.querySelector(".nav-services-dropdown");
	const caret = item.querySelector(".nav-services-dropdown__caret");

	if (!dropdown) {
		return null;
	}

	let openTl = null;
	let closeTl = null;

	function killTimelines() {
		if (openTl) {
			openTl.kill();
			openTl = null;
		}
		if (closeTl) {
			closeTl.kill();
			closeTl = null;
		}
	}

	/** Close this panel only (no backdrop) — used when switching to a sibling mega item. */
	function closeInstant() {
		killTimelines();
		if (!item.classList.contains("is-open")) {
			return;
		}
		item.classList.remove("is-open");
		dropdown.setAttribute("aria-hidden", "true");
		gsap.set(dropdown, { autoAlpha: 0, pointerEvents: "none" });
		if (caret) {
			gsap.set(caret, { autoAlpha: 0 });
		}
	}

	function open() {
		clearGlobalLeaveTimer();

		allMegaItems.forEach((other) => {
			if (other !== item && other.classList.contains("is-open")) {
				const api = itemApis.find((x) => x.item === other);
				api?.closeInstant();
			}
		});

		if (openTl && openTl.isActive()) {
			return;
		}
		if (closeTl) {
			closeTl.kill();
			closeTl = null;
		}
		if (openTl) {
			openTl.kill();
			openTl = null;
		}

		item.classList.add("is-open");
		dropdown.setAttribute("aria-hidden", "false");

		const dropdownWasVisible =
			parseFloat(String(gsap.getProperty(dropdown, "opacity"))) > 0.12;
		const backdropAlreadyOn = backdropIsVisiblyOn(backdrop);

		if (!dropdownWasVisible) {
			if (backdrop && !backdropAlreadyOn) {
				gsap.set(backdrop, { autoAlpha: 0 });
			}
			gsap.set(dropdown, {
				autoAlpha: 0,
				pointerEvents: "none",
			});

			if (caret) {
				gsap.set(caret, { autoAlpha: 0 });
			}
		}

		openTl = gsap.timeline();
		if (backdrop && !backdropAlreadyOn) {
			openTl.to(
				backdrop,
				{
					autoAlpha: 1,
					duration: 0.26,
					ease: EASE_OUT,
				},
				0
			);
		}
		openTl.to(
			dropdown,
			{
				autoAlpha: 1,
				duration: 0.28,
				ease: EASE_OUT,
				pointerEvents: "auto",
			},
			0.03
		);
		if (caret) {
			openTl.to(
				caret,
				{
					autoAlpha: 1,
					duration: 0.18,
					ease: EASE_OUT,
				},
				0.05
			);
		}
	}

	function closeFull() {
		clearGlobalLeaveTimer();
		if (!item.classList.contains("is-open")) {
			return;
		}

		killTimelines();

		closeTl = gsap.timeline({
			onComplete: () => {
				item.classList.remove("is-open");
				dropdown.setAttribute("aria-hidden", "true");
				gsap.set(dropdown, { pointerEvents: "none" });
				closeTl = null;
			},
		});

		closeTl.to(
			dropdown,
			{
				autoAlpha: 0,
				duration: 0.16,
				ease: EASE_IN,
				pointerEvents: "none",
			},
			0.01
		);
		if (caret) {
			closeTl.to(
				caret,
				{
					autoAlpha: 0,
					duration: 0.14,
					ease: EASE_IN,
				},
				0
			);
		}
		if (backdrop) {
			closeTl.to(
				backdrop,
				{
					autoAlpha: 0,
					duration: 0.22,
					ease: EASE_IN,
				},
				0.03
			);
		}
	}

	function scheduleClose() {
		clearGlobalLeaveTimer();
		globalLeaveTimer = window.setTimeout(() => {
			globalLeaveTimer = null;
			if (isAnyMegaItemHovered()) {
				return;
			}
			closeFull();
		}, LEAVE_DELAY_MS);
	}

	item.addEventListener("mouseenter", () => {
		if (!isDesktopMegaNav()) {
			return;
		}
		open();
	});
	item.addEventListener("mouseleave", () => {
		if (!isDesktopMegaNav()) {
			return;
		}
		scheduleClose();
	});
	item.addEventListener("focusin", () => {
		if (!isDesktopMegaNav()) {
			return;
		}
		open();
	});
	item.addEventListener("focusout", (e) => {
		if (!isDesktopMegaNav()) {
			return;
		}
		if (!item.contains(e.relatedTarget)) {
			scheduleClose();
		}
	});

	return {
		item,
		open,
		closeInstant,
		closeFull,
		scheduleClose,
	};
}

function hideBackdropOnMobile(backdrop) {
	if (!backdrop || isDesktopMegaNav()) {
		return;
	}

	gsap.killTweensOf(backdrop);
	gsap.set(backdrop, { autoAlpha: 0, pointerEvents: "none" });
	itemApis.forEach((api) => api.closeInstant());
}

export function initNavServicesDropdown() {
	const items = document.querySelectorAll(MEGA_TRIGGER_SELECTOR);
	const backdrop = document.querySelector(".nav-services-backdrop");
	const allMegaItems = Array.from(items);

	if (!allMegaItems.length) {
		return;
	}

	itemApis = allMegaItems
		.map((item) => createItemApi(item, backdrop, allMegaItems))
		.filter(Boolean);

	hideBackdropOnMobile(backdrop);

	window.matchMedia(DESKTOP_MQ).addEventListener("change", () => {
		hideBackdropOnMobile(backdrop);
	});
}
